// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartKPIContent.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartKPIContent model
 */

import { action, computed, extendObservable, observable, reaction, toJS } from 'mobx';
import ReportPartContent from '../Izenda.ReportPartContent';
import {
  assign,
  compact,
  concat,
  filter,
  find,
  findIndex,
  flatMap,
  flow,
  get,
  getOr,
  includes,
  invoke,
  isEmpty,
  isFunction,
  isNil,
  isUndefined,
  omit,
  placeholder,
  size,
  some,
  uniqueId,
  head
} from 'lodash/fp';

import tracker from 'share/tracking/Izenda.Tracker';
import {
  addContainers,
  checkCompatibleFunctionCommon,
  getDefaultFunction,
  getElements,
  hasElement,
  populateReportChartContainer
} from 'share/utils/Izenda.ReportPartUtils';
import { EMPTY_FUNCTION, SORT_ORDERS } from 'logic/data/Izenda.ReportPartFieldConstants';
import { EXPANDED_LEVEL, REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import CONSTANTS from 'logic/data/Izenda.ReportPartKPIConstants';
import { AVAILABLE_FUNCTION_CONFIG } from 'logic/data/Izenda.GaugeAvailableFunction';
import GaugeValueDetailsModel from '../Izenda.ReportPartGaugeValueDetails';
import { exportClass } from 'share/Izenda.ServiceProvider';
import { Highcharts as DefaultVizEngine } from 'share/components/charting/highcharts/Izenda.HighchartsVizNames';
import ReportPartKPITile from './Izenda.ReportPartKPITile';


const { ContainerName, TileTypes } = CONSTANTS;
const NUMBER_ZERO = 0;
const NUMBER_ONE = 1;
const NUMBER_CHECK_INDEX = -1;

@exportClass('ReportPartKPIContent')
@tracker
export default class ReportPartKPIContent extends ReportPartContent {
  @observable isCrossFiltering = false;
  @observable title = null;
  @observable description = null;
  @observable
  properties = {
    chartType: CONSTANTS.DefaultChartType,
    vizEngine: DefaultVizEngine,
    colorTheme: undefined,
    isDefaultTheme: true,
    optionByType: {
      metricTile: []
    },
    view: {
      ...CONSTANTS.KPI_REPORT_PART_PROPERTIES,
      dataRefreshInterval: {
        enable: false,
        updateInterval: 0,
        isAll: true,
        latestRecord: 0
      }
    },
    printing: {
      izPageBreakAfterSeparator: false
    },
    backgroundImage: {
      canvasData: null,
      dataUrl: '',
      imgSrc: '',
      resetZoom: true
    }
  };

  // use to show/hide fields which need to draw chart - do not save
  @observable activeSerie = null;

  // use to show/hide properties
  @observable activeTile = null;

  @observable gridData = [];
  @observable hasMetricTile = false;

  @observable reportWidth = 0;
  @observable reportHeight = 0;

  @observable minGridRows = 4;
  @observable minGridCols = 4;

  @observable needUpdateLayoutStore = false;

  constructor(reportPartContent) {
    super(REPORT_PART_TYPE.KPI);

    this.staticProperties = {};
    const props = {};
    // important - these containers save list of fields to get data and render report
    addContainers(props, ContainerName.VALUES);

    extendObservable(this, props);

    reportPartContent && this.readData(reportPartContent);

    this.cleanDirty();
  }

  @computed
  get chartType() {
    return this.properties.chartType;
  }

  @computed
  get serieChartList() {
    const valuesElements = this.values.visibleElement || [];
    return valuesElements.map(elm => elm);
  }

  @computed
  get activeElmsByContainers() {
    const activeSerieKey = get('fieldName', this.activeSerie);

    return {
      values: getElements.call(this, this.values, activeSerieKey)
    };
  }

  @computed
  get containerCollection() {
    return compact([this.values]);
  }

  @computed
  get isBeingBuild() {
    return hasElement(this.values);
  }

  @computed
  get dataRefreshInterval() {
    return get('view.dataRefreshInterval', this.properties);
  }

  @computed
  get reportPartStyle() {
    return this.properties.chartType;
  }

  @computed
  get vizEngine() {
    return this.properties.vizEngine;
  }

  get defaultVizEngine() {
    return DefaultVizEngine;
  }

  @action
  setVizEngine(value: string) {
    this.properties.vizEngine = value;
  }

  @computed
  get valuesElements() {
    return this.values.visibleElement;
  }

  @computed
  get allFieldCollection() {
    return flatMap(item => (item.elements ? item.elements.slice() : []), this.containerCollection);
  }

  @computed
  get isShowMetric() {
    return !isEmpty(this.serieChartList) && this.serieChartList.length > NUMBER_ONE;
  }

  /**
   * Determine whether the report part content automatically sizes its container or not
   */
  get autoSize() {
    return true;
  }

  /**
   * validate function based on container
   * @method validateFunction
   * @param {Object} container Container need validateFunction
   * @param {Function} functionInfo Function need validate
   * @return {Object}
   */
  validateFunction(container: any, functionInfo: () => mixed): any {
    if (!functionInfo) {
      return {
        status: false,
        useDefaultFunction: false
      };
    }

    return {
      status: true
    };
  }

  /**
   * Get Default Function And Format for field in container
   * @method getDefaultFunctionFormat
   * @param {Object} container Container have element need get default dataType
   * @param {string} dataType  Data Type for field
   * @return {Object}
   */
  getDefaultFunctionFormat(container: any, dataType: string): any {
    const byContainer = CONSTANTS.DefaultFunction[this.chartType][container.name || ''] || {};
    return getDefaultFunction(byContainer, dataType);
  }

  /**
   * read data from api/create report part to update data
   * @param {Object} content [data from api/create report part]
   */
  @action
  readData(content: any): void {
    content = content || {};
    const properties = content.properties || {};
    this.isCrossFiltering = content.isCrossFiltering;
    const gridData = toJS(content.gridData);

    gridData.forEach((data) => {
      this.addGridData(data.tileType, data);
    });

    this.setActiveTile(this.gridData[0]);

    this.values = populateReportChartContainer(content.values, 'values', this.afterInsertFieldToContainer.bind(this));

    this.settings = content.settings;

    // report part properties
    this.setChartType(properties.chartType);

    // report part properties
    this.staticProperties = get('staticProperties', properties);
    this.setReportPartProperties(omit('staticProperties', properties), true);
    this.setActiveSerie(null, find({ fieldName: content.selectedMetric }, this.serieChartList));
  }

  /**
   * Build data for save
   * @return {Object}
   */
  @action
  buildDataForSave(): any {
    const chartContent = toJS(omit(['properties', 'staticProperties'], this));

    // merge static properties
    chartContent.properties = this.getReportPartProperties();
    chartContent.selectedMetric = get('activeSerie.fieldName', this);
    return chartContent;
  }

  /**
   * @method setDimensions
   * @description set kpi grid dimensions in data to be used in background image property modal
   * @param {number} width width of kpi grid
   * @param {number} height height of kpi grid
   */
  @action
  setDimensions(width, height) {
    this.reportWidth = width;
    this.reportHeight = height;
  }

  /**
   * Set data by data key
   * @param {string} key
   * @param {Object} newVal
   */
  setDataByKey(key: string, newVal: any): void {
    this[key] = newVal;
    this.markDirty();
  }

  /**
   * Get data by data key
   * @param {string} key
   * @return {Object}
   */
  getDataByKey(key): any {
    return this[key];
  }

  /**
   * Clean dirty status.
   */
  cleanDirty(): void {
    this.resetDirty();
  }

  /**
   * get properties of report part, if propKey is null, return all properties
   * @param {string} propKey
   * @return {Object}
   */
  getReportPartProperties(propKey: string): any {
    const props = toJS(this.properties) || {};

    if (!propKey) {
      return assign({ staticProperties: this.staticProperties }, props);
    }

    return (
      get(propKey, props.commonOptions || {}) ||
      get(propKey, props.optionByType || {}) ||
      get(propKey, props.view || {}) ||
      get(propKey, props.printing || {}) ||
      get(propKey, props.kpi || {}) ||
      get(propKey, this.defaultCommonOptions || {}) ||
      get(propKey, this.defaultOptionsByType || {}) ||
      get(propKey, props || {})
    );
  }

  /**
   * @method checkAdditionalGridDimensions
   * @description make sure that the kpi grid rows and cols property matches the number of rows and columns
   * that show on the grid. This covers the case where a user adds a tile in the last couple rows or columns
   * @param {number} maxRows The maximum number of rows that are actually needed to show all grid items
   * @param {number} maxCols The maximum number of cols that are actually needed to show all grid items
   */
  checkAdditionalGridDimensions(maxRows, maxCols) {
    const gridDimensions = this.getReportPartProperties('view');
    const { numRows, numCols } = gridDimensions;
    const newProps = {
      kpi: {
        ...gridDimensions,
        numRows: numRows < maxRows ? maxRows : numRows,
        numCols: numCols < maxCols ? maxCols : numCols
      }
    };
    this.setReportPartProperties(newProps, true);
    this.setMinRowsCols(maxRows, maxCols);
  }

  /**
   * Get metric container
   */
  getMetricContainer() {
    return ContainerName.VALUES;
  }

  /**
   * @method setMinRowsCols
   * @description set the minimun number of rows and cols for numRow and numCol property based on the max number
   * of rows and cols absolutely needed to display all grid items
   * @param {number} maxRows maxItemRows, max number of grid rows needed to show all grid items, from KPIGridLayoutStore
   * @param {number} maxCols maxItemCols, max number of grid cols needed to show all grid items, from KPIGridLayoutStore
   */
  @action
  setMinRowsCols(maxRows, maxCols) {
    this.minGridRows = maxRows > 4 ? maxRows : 4;
    this.minGridCols = maxCols > 4 ? maxCols : 4;
  }

  /**
   * @method setNeedUpdateLayoutStore
   * @description set needUpdateLayoutStore
   * @param {boolean} needsUpdate indicator for if KPI Grid layout store needs an update
   */
  @action
  setNeedUpdateLayoutStore(needsUpdate) {
    this.needUpdateLayoutStore = needsUpdate;
  }

  /**
   * set properties of report part
   * @param {Object} newProps
   */
  @action
  setReportPartProperties(newProps: any, isloading = false): void {
    const props = toJS(this.properties) || {};
    newProps = toJS(newProps);
    extendObservable(props, newProps);
    this.properties = props;
    if (!isUndefined(newProps.colorTheme) || newProps.isDefaultTheme) {
      props.isDefaultTheme = newProps.isDefaultTheme;
      props.colorTheme = newProps.colorTheme;
    }
    !isloading && this.markDirty();
  }

  /**
   * Get dirty status.
   * @returns {boolean}
   */
  checkDirty(): boolean {
    const dirtyElements = flow(
      flatMap(item => (item.elements ? item.elements.peek() : [])),
      filter(element => {
        const metric = get('properties.metric', element);
        return (element.checkDirty && element.checkDirty()) || (metric && metric.checkDirty && metric.checkDirty());
      })
    )(this.containerCollection);

    const properties = this.properties || {};
    const metric = properties.metric;

    return (
      this.isDirty ||
      !isEmpty(dirtyElements) ||
      (properties.checkDirty && properties.checkDirty()) ||
      (metric && metric.checkDirty && metric.checkDirty())
    );
  }

  /**
   * Set Chart type
   * @param {string} nextType
   */
  @action
  setChartType(nextType: string): void {
    const previousType = this.properties.chartType;
    this.properties.chartType = nextType;
    this.convertChartType(previousType, nextType);

    this.markDirty();
  }

  /**
   * Convert chart type
   * @param {string} previousType
   * @param {string} nextType
   */
  @action
  convertChartType(previousType: string, nextType: string): void {
    switch (nextType) {
      case CHART_TYPE.SimpleGauge:
        initValuesData.call(this, 'resetScaleData');
        break;
      default:
        break;
    }

    this.markDirty();

    function initValuesData(fnName) {
      let fieldMetricVal;

      this.values.elements.map(field => {
        fieldMetricVal = getOr({}, 'properties.metric', field);
        isFunction(fieldMetricVal[fnName]) && fieldMetricVal[fnName]();
        invoke('cleanDirty', fieldMetricVal);
      });
    }
  }

  /**
   * @method setHasMetricTile
   * @description set and reset indictor for if the KPI has a metric tile, which is used to control what
   * tiles user can add to the KPI
   * @param {string} tileType tile type of the tile currently being interacted with
   * @param {boolean} shouldSet indicator for if the flag should be set (true) or reset (false)
   */
  @action
  setHasMetricTile(tileType, shouldSet) {
    if (tileType === TileTypes.metric.id) {
      this.hasMetricTile = shouldSet;
    }
  }

  /**
   * @method addNewGridData
   * @description insert a grid item tile into the KPI
   * @param {String} type type of tile that will be added to the grid
   * @param {object} data any tile data that needs to be assigned to the new instance of ReportPartKPITile
   * @param {boolean} isNew indicator for if the tile is newly added to the grid
   */
  @action
  addGridData(tileType, data, isNew = false) {
    const idNum = this.gridData.length;

    this.setHasMetricTile(tileType, true);

    const tile = new ReportPartKPITile({
      ...data,
      tileType,
      id: uniqueId('reportPartKPI_'),
      isNew
    });
    this.gridData.push(
      tile
    );
    const activeTile = this.gridData[this.gridData.length - 1];
    this.setActiveTile(activeTile);
    tileType === TileTypes.metric.id && this.properties.optionByType.metricTile.push(this.getMetricTile(tile));
    this.markDirty();
  }

  /**
 * get metric tile
 * @param {object} tile information of tile - Metric
 * @returns {metrictile}
 */
  getMetricTile(tile) {
    const metricTile = {
      tile: tile,
      tileProperties: tile.properties.getStyle && tile.properties.getStyle()
    };

    return metricTile;
  }

  /**
 * get metric tile list
 * @returns {metricTileList}
 */
  getMetricTileList() {
    const tile = head(filter(function (item) {
      return item.tileType === TileTypes.metric.id;
    }, this.gridData.toJS()));
    const metricTile = isNil(tile) ? [] : [this.getMetricTile(tile)];
    return metricTile;
  }

  /**
   * @method updateGridDataPosition
   * @description update position of a tile in the grid
   * @param {object} position x and y coordinates of the tile
   */
  @action updateGridDataPosition(position) {
    let isChangedValue = false;
    this.gridData.map(data => {
      if (data.id === position.id) {
        if (data.height !== position.height
          || data.width !== position.width
          || data.left !== position.left
          || data.top !== position.top) {
          isChangedValue = true;
        }
        data.updatePosition(position);
      }
    });
    isChangedValue && this.markDirty();
  }

  /**
   * @method setActiveTile
   * @description set the active tile to update tile properties panel
   * @param {ReportPartKPITile} tile current tile that is being edited
   */
  @action
  setActiveTile(tile) {
    this.activeTile = tile;
  }

  /**
   * @method deleteActiveTile
   * @description delete the currently active tile and reset properties is necessary
   */
  @action
  deleteActiveTile() {
    this.gridData = this.gridData.filter((tile) => this.activeTile.id !== tile.id);
    this.setHasMetricTile(this.activeTile.tileType, false);
    this.activeTile = null;
  }

  /**
   * Set active serie key, used to show/hide serie chart in metric dropdown
   * @param {string} activeSerieKey
   * @param {Object} activeSerieInfo
   */
  setActiveSerie(activeSerieKey: string, activeSerieInfo: any): void {
    this.activeSerie = activeSerieInfo;
    this.markDirty();
  }

  /**
   * If element is inserted to values container, need to init a model
   * to manage its information: scale, threshold, supplementary KPI
   * @param {Object} container
   * @param {Object} newElement
   * @param {Object} rawData
   */
  @action
  afterInsertFieldToContainer(container: any, newElement: any, rawData: any): void {
    if (container.name === ContainerName.VALUES) {
      const metric = new GaugeValueDetailsModel(getOr({}, 'properties.metric', rawData));
      newElement.properties.metric = metric;
    }
  }

  /**
   * This properties is used to save the page size
   * Only use for chart-type which supported pagination: pie, donut, treemap, funnel
   * @param {number} pageSize
   */
  setPageSize(pageSize: number): void {
    this.staticProperties.pageSize = pageSize;
  }

  /**
   * Check need to break page when printing.
   * @returns {boolean}
   */
  checkBreakPage(): boolean {
    return this.properties.printing.izPageBreakAfterSeparator;
  }

  /**
   * check if function is compatible with element
   * @method checkedCompatibleFunction
   * @param {Object} container Container that element belong
   * @param {Object} element Element need to validate
   * @param {Function} functionInfo Function infomation
   * @return {boolean}
   */
  checkedCompatibleFunction(container: any, element: any, functionInfo: () => mixed): boolean {
    if (!container) {
      return true;
    }

    let result = true;
    const functionName = getOr(EMPTY_FUNCTION.name, 'name', functionInfo);

    // If the user add a calculated field which contains the aggregated field
    // then the user cannot select any aggregated field in Function dropdown in
    // Field Properties Panel. The user can select “empty” function or other user
    // defined functions which has function level = field.
    if (element.field.hasAggregatedFunction) {
      result = functionInfo.userDefined || !functionName;
    }

    const checkDataTypeNumericMoney = includes(placeholder, ['Numeric', 'Money']);

    if (result && [ContainerName.VALUES].indexOf(container.name) > NUMBER_CHECK_INDEX) {
      // function return Money or Numeric
      result =
        checkDataTypeNumericMoney(functionInfo.formatDataType) ||
        // Element data type is Money or numeric and function is empty function
        (checkDataTypeNumericMoney(element.fieldDataType) && !functionName);
    }

    let config = AVAILABLE_FUNCTION_CONFIG[this.chartType];

    config = config && config[container.name];

    if (!config || !result) {
      return result;
    }

    return checkCompatibleFunctionCommon(functionInfo, element, config);
  }

  /**
   * Reset refresh interval setting to default vale
   * @return {Object} default value
   */
  @action
  resetRefreshIntervalValue(): any {
    this.properties.view.dataRefreshInterval = CONSTANTS.DEFAULT_SETTING_VALUE.refreshIntervalDefault;
    this.markDirty();
    return this.properties.view.dataRefreshInterval;
  }

  /**
   * Reset background color setting to default value
   */
  @action('resetBackgroundColorDefaultValue')
  resetBackgroundColorDefaultValue(): void {
    this.properties.commonOptions['izEntire.backgroundColor'] =
      CONSTANTS.DEFAULT_SETTING_VALUE.backGroundColorDefault['izEntire.backgroundColor'];
    this.properties.commonOptions['izPlot.backgroundColor'] =
      CONSTANTS.DEFAULT_SETTING_VALUE.backGroundColorDefault['izPlot.backgroundColor'];
    this.markDirty();
  }

  /**
   * Reset border setting to default value
   * @return {Object} default value
   */
  @action
  resetBorderDefaultValue(): any {
    this.properties.commonOptions = CONSTANTS.DEFAULT_SETTING_VALUE.borderDefault;
    this.markDirty();
    return this.properties.commonOptions;
  }

  /**
   * Return drilldown info: 
   * @return {Object}
   */
  getDrilldownInfo(): any {
    return this.drilldownInfo = {
      filters: [],
      separatorValue: null
    };
  }

}



// WEBPACK FOOTER //
// ./ui/logic/models/ReportPartKPIModels/Izenda.ReportPartKPIContent.js